import React, { ChangeEvent, Component, MouseEventHandler } from "react";
import "./OwSelectOption.scss";
import * as _ from "lodash-es";
import { IntlShape, WrappedComponentProps } from "react-intl";

type Props = {
    intl: IntlShape;
    options?: any[];
    filterOptionsFn?: (selectedValue: any) => any[];
    valueProp: string;
    showBlankFirst?: boolean;
    selectedOption?: any;
    selectedOptionProp?: string;
    titleProp: string;
    titlePropTwo?: string;
    titleRenderFn?: (option: any) => string;
    fn: ((e: ChangeEvent<HTMLSelectElement>) => void) | ((e: any) => void);
    name: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    isActive?: boolean;
    isDisabledOnActive?: boolean;
    autoSelectSingleOption?: boolean;
    hasSelectedOptionProp?: boolean;
    emitObject?: boolean;
    emitOnLoad?: boolean;
    emitTarget?: boolean;
    title?: string;
    nullIfBlank?: boolean; // not working?
    translateTitle?: boolean;
    className?: string;
    id?: string;
    sizeClass?: string;
    testId?: string;
};

type State = {
    hasLoaded: boolean;
    optionsLength: number;
    selectedValue?: any;
    hasDoneInitialEmit?: boolean;
};

/**
 * @name OwSelectOptionContent
 * OwSelectOption
 *
 * In using this component you want to make sure that the selectedOption and selectedOptionProp (assuming an object)
 * are passed in to auto-select the selected option.
 *
 * It will compare the valueProp (such as guid) with the
 * selectedOption[selectedOptionProp] to determine if the selected value should be selected.
 */
export class OwSelectOptionContent extends Component<Props & WrappedComponentProps, State> {
    public static defaultProps = {
        showBlankFirst: true,
    };

    constructor(props: any) {
        super(props);

        this.state = {
            hasLoaded: false,
            optionsLength: this.props.options ? this.props.options.length : 0,
            hasDoneInitialEmit: false,
            selectedValue: this.setSelectedValue(true),
        };
    }

    componentDidMount() {
        this.setSelectedValue();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (
            prevProps.selectedOption !== this.props.selectedOption ||
            prevProps.isActive !== this.props.isActive ||
            (prevProps.options || []).length !== (this.props.options || []).length
        ) {
            this.setSelectedValue();
        }
    }

    setSelectedValue = (onlyReturnValue?: boolean) => {
        // console.log(`CDM - ${this.props.name}`);
        let selectedValue: any = undefined;
        let isNumberValue = false;

        const propSelectedOption: any = this.props.selectedOption;
        let propSelectedOptionProp: any = _.get(this.props, "selectedOptionProp", "id");

        if (this.props.hasSelectedOptionProp === false) {
            propSelectedOptionProp = null;
        }

        _.each(this.props.options, (option, index) => {
            const value = _.get(option, `${this.props.valueProp}`, "");

            if (_.isNumber(value)) {
                isNumberValue = true;
            }

            // console.log("selectedOption", propSelectedOption);

            if (!_.isNil(propSelectedOption)) {
                if (this.props.emitObject) {
                    selectedValue = this.props.selectedOption;
                } else {
                    if (!_.isNil(propSelectedOptionProp) && propSelectedOptionProp.length > 0) {
                        selectedValue = propSelectedOption[propSelectedOptionProp];
                    } else {
                        selectedValue = propSelectedOption;
                    }
                }

                // console.log(
                //     "selectedValue",
                //     selectedValue,
                //     "selectedOption: ",
                //     propSelectedOption,
                //     "selectedOptionProp: ",
                //     propSelectedOptionProp
                // );

                // This prevents us from dealing with the selectedValue being a string, but the provided option
                // values are numbers.
                if (isNumberValue && !_.isNumber(selectedValue) && !_.isObject(selectedValue)) {
                    selectedValue = parseInt(selectedValue);
                }

                // console.log("Value: ", value, "Selected Value: ", selectedValue, "selectedOption: ", propSelectedOption);
            } else {
                /* istanbul ignore else*/
                if (this.props.autoSelectSingleOption) {
                    /* istanbul ignore next*/
                    if (this.props.options && this.props.options.length === 1) {
                        // selectedValue = value; // auto selecting the first value...don't
                    }
                }
            }
        });

        // console.log("selectedValue", selectedValue, this.props.emitOnLoad);
        if (onlyReturnValue) {
            return selectedValue;
        } else {
            this.setState(
                {
                    selectedValue,
                    // hasDoneInitialEmit: true,
                },
                () => {
                    if (!this.props.isActive) {
                        if (this.props.emitOnLoad && !this.state.hasDoneInitialEmit) {
                            // console.log("emitOnLoad", this.props.name, this.state.selectedValue);
                            if (this.props.emitObject && !this.props.emitTarget) {
                                this.props.fn(this.props.selectedOption);
                            } else {
                                if (!this.props.emitTarget) {
                                    this.props.fn(this.state.selectedValue);
                                } else {
                                    this.props.fn({ target: { name: this.props.name, value: this.state.selectedValue } } as any);
                                }
                            }

                            this.setState({
                                hasDoneInitialEmit: true,
                            });
                        } else if (this.props.autoSelectSingleOption && !this.state.hasDoneInitialEmit) {
                            this.setState({
                                hasDoneInitialEmit: true,
                            });
                        }
                    }
                }
            );
        }
    };

    fn = (e: ChangeEvent<HTMLSelectElement>) => {
        /* istanbul ignore else*/
        if (!_.isNil(e.target) && !_.isNil(e.target.value)) {
            let emitVal = e;

            if (this.props.emitObject && this.props.options) {
                for (const option of this.props.options) {
                    let value: any = e.target.value;

                    /* istanbul ignore else*/
                    if (_.isNumber(option[this.props.valueProp])) {
                        value = Number(value);
                    }

                    if (option[this.props.valueProp] === value) {
                        emitVal = option;
                    }
                }
            }

            if (this.props.nullIfBlank) {
                if (!e.target.value.length) {
                    if (this.props.emitObject) {
                        // @ts-expect-error
                        emitVal = null;
                        // @ts-expect-error
                        e = null;
                    } else {
                        // @ts-expect-error
                        e.target.value = null;
                    }
                }
            }

            this.props.fn(emitVal);
        }

        this.setState({
            selectedValue: e ? e.target.value : e,
        });
    };

    getOptions = (selectedValue: any) => {
        let availableOptions = this.props.options;

        if (this.props.filterOptionsFn) {
            availableOptions = this.props.filterOptionsFn(selectedValue);
        }

        const options = (availableOptions || []).map((option, index) => {
            let isNumberValue = false;

            const value = _.get(option, `${this.props.valueProp}`, "");
            let title = _.get(option, `${this.props.titleProp}`, "");

            if (this.props.translateTitle && title) {
                title = this.props.intl.formatMessage({ id: title.toLowerCase(), defaultMessage: `_${title}_` });
            }

            if (_.isNumber(value)) {
                isNumberValue = true;
            }

            // console.log('selectedOption', this.props.selectedOption);

            if (!_.isNil(this.state.selectedValue)) {
                if (!_.isNil(this.props.selectedOptionProp) && _.isObject(this.state.selectedValue)) {
                    // @ts-expect-error
                    selectedValue = this.state.selectedValue[this.props.selectedOptionProp];
                } else {
                    selectedValue = this.state.selectedValue;
                }

                // This prevents us from dealing with the selectedValue being a string, but the provided option
                // values are numbers.
                if (isNumberValue && !_.isNumber(selectedValue) && !_.isNil(selectedValue)) {
                    selectedValue = parseInt(selectedValue);
                }

                // console.log("Value: ", value, "Selected Value: ", selectedValue);
            } else {
                /* istanbul ignore else*/
                if (!this.props.isActive) {
                    if (this.props.autoSelectSingleOption) {
                        /* istanbul ignore next */
                        if (this.props.options && this.props.options.length === 1) {
                            /* istanbul ignore next*/

                            if (!this.state.hasDoneInitialEmit && value) {
                                selectedValue = value; // auto selecting the first value, dont
                                this.fn({ target: { name: title, value } } as any);
                            }
                        }
                    }
                }
            }

            let title_two = "";

            if (!_.isNil(this.props.titlePropTwo)) {
                title_two = " " + _.get(option, `${this.props.titlePropTwo}`, "");
            }

            let keyIndex = index;

            // in the event that there's a first blank option
            // in order not to mess up the key values for the options
            // we must account for the first being blank.
            if (this.props.showBlankFirst) {
                keyIndex += 1;
            }

            let titleSet = title + title_two;

            if (this.props.titleRenderFn) {
                titleSet = this.props.titleRenderFn(option);
            }

            return (
                <option key={keyIndex} value={value}>
                    {titleSet}
                </option>
            );
        });

        return { options, selectedValue };
    };

    onMouseDown = (e: any) => {
        e.stopPropagation();
    };

    handleClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };

    render() {
        const optionsDetails = this.getOptions(this.state.selectedValue);

        const options = optionsDetails.options;
        let selectedValue = optionsDetails.selectedValue;

        // console.log(`${this.props.name} selectedValue`, selectedValue, options);

        // let defaultValue = selectedValue;

        let blankOption;

        if (this.props.showBlankFirst) {
            blankOption = <option key={0} value={undefined} />;
            options.unshift(blankOption);
        }

        let invalidClass = "";

        if (this.props.isRequired) {
            invalidClass = "is-invalid alert-danger";

            if (selectedValue) {
                invalidClass = "";
            }
        }

        if (_.isNaN(selectedValue)) {
            selectedValue = "";
        }

        // if (_.isNil(selectedValue)) {
        //     selectedValue = "";
        // }

        let sizeClass = "form-select-sm";

        if (this.props.sizeClass) {
            sizeClass = this.props.sizeClass;
        }

        return (
            <span className={"OwSelectOption"}>
                <select
                    id={this.props.id}
                    title={this.props.title}
                    disabled={this.props.isDisabled || (this.props.isDisabledOnActive && this.props.isActive)}
                    required={this.props.isRequired}
                    value={selectedValue ?? ""}
                    className={(this.props.className || "") + " form-select " + " " + sizeClass + " " + invalidClass}
                    onChange={(e: any) => {
                        this.fn(e);
                    }}
                    onClick={this.handleClick}
                    onMouseDown={this.onMouseDown}
                    name={this.props.name}
                    data-testid={this.props.testId || this.props.name}>
                    {options}
                </select>
                {this.props.isActive ? <i className={"far fa-sync fa-spin text-dark"} /> : null}
            </span>
        );
    }
}

export default OwSelectOptionContent;
